import { AsYouType, CountryCode } from 'libphonenumber-js'

const hasPhoneSeparators = (value: string) => /[\s\-()]/.test(value)

/**
 * Removes the domestic trunk prefix (e.g. leading 0 for CH) when the country
 * calling code is already selected separately. Otherwise +41 079… would be
 * invalid / redundant with the international prefix.
 */
export const stripNationalTrunkPrefix = (
    nationalDigits: string,
    countryCode: CountryCode | undefined,
    callingCodePrefix: string
): string => {
    if (!nationalDigits || !countryCode || !callingCodePrefix) {
        return nationalDigits
    }

    const callingCodeDigits = callingCodePrefix.replace(/\D/g, '')
    const normalizedNationalDigits = nationalDigits.startsWith(callingCodeDigits)
        ? nationalDigits.slice(callingCodeDigits.length)
        : nationalDigits

    const formatter = new AsYouType(countryCode)
    formatter.input(callingCodePrefix + normalizedNationalDigits)
    return formatter.getNationalNumber()
}

/**
 * Formats a national phone number as the user types.
 * When the country calling code is selected separately, users often omit the
 * national trunk prefix (e.g. leading 0 for CH). National AsYouType alone then
 * skips spacing — fall back to international formatting and strip the prefix.
 */
export const formatNationalPhoneAsYouType = (
    nationalDigits: string,
    countryCode: CountryCode | undefined,
    callingCodePrefix: string
): string => {
    const digits = stripNationalTrunkPrefix(
        nationalDigits,
        countryCode,
        callingCodePrefix
    )
    if (!digits) {
        return ''
    }
    if (!countryCode) {
        return digits
    }

    const nationalFormatted = new AsYouType(countryCode).input(digits)
    if (hasPhoneSeparators(nationalFormatted)) {
        return nationalFormatted
    }

    const intlFormatted = new AsYouType(countryCode).input(
        callingCodePrefix + digits
    )
    const escapedPrefix = callingCodePrefix.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
    )
    const stripped = intlFormatted.replace(
        new RegExp(`^${escapedPrefix}\\s?`),
        ''
    )

    if (hasPhoneSeparators(stripped)) {
        return stripped
    }

    return nationalFormatted
}
